import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Input, Modal, PageHead, Toggle, useToast } from '@w7/ui'
import { sessionApi } from '@/api/sessionApi'
import { useShortKey } from '@/app/ShortKeyContext'
import { getUserProfile } from '@/auth/session'
import { ScoreDashboard } from './ScoreDashboard'
import { MAX_LIMIT_OPTIONS, type Market, type RateItem, type RunItem, type SubMarket } from './types'
import { nextMarketFlags } from './fancyToggleState'
import {
  canStartCreateMarketSave,
  shouldIgnoreMarketSuspendHotkeys,
  shouldSubmitCreateMarketOnEnter,
} from './sessionMarketHotkeys'
import '@/styles/app.css'

type CreateForm = {
  selectMarket: Market | null
  subMarketName: string
  maxBet: number
  minBet: number
  maxMarketAmountFancy: number
  maxLimitsType: string
  suspendMaxLimit: number | string
  notification: string
}

export function MatchesDashboard() {
  const toast = useToast()
  const { subMarketkey } = useShortKey()
  const [markets, setMarkets] = useState<Market[]>([])
  const [selectMarket, setSelectMarket] = useState<Market | null>(null)
  const [selected, setSelected] = useState(false)
  const [subMarkets, setSubMarkets] = useState<SubMarket[] | 'No SubMarket Found'>([])
  const [rateList, setRateList] = useState<RateItem[]>([])
  const [runList, setRunList] = useState<RunItem[]>([])
  const [accessLevel, setAccessLevel] = useState('')
  const [reloadPage, setReloadPage] = useState(() => {
    try {
      const raw = localStorage.getItem('reloadStatus')
      if (raw === 'true' || raw === '1') return true
      if (!raw || raw === 'false' || raw === '0') return false
      return Boolean(JSON.parse(raw))
    } catch {
      return false
    }
  })
  const [counter, setCounter] = useState(20)
  const [createOpen, setCreateOpen] = useState(false)
  const [createSaving, setCreateSaving] = useState(false)
  const createSavingRef = useRef(false)
  const [form, setForm] = useState<CreateForm>({
    selectMarket: null,
    subMarketName: '',
    maxBet: 0,
    minBet: 1,
    maxMarketAmountFancy: 0,
    maxLimitsType: 'Minor',
    suspendMaxLimit: 0,
    notification: '',
  })
  const [incorrectBet, setIncorrectBet] = useState(false)
  const [marketFlags, setMarketFlags] = useState<{
    isMarketSuspended: boolean
    ballChalu: boolean
    betDelay?: number | string
    _id?: string
  }>({ isMarketSuspended: false, ballChalu: false })
  const [skyObj, setSkyObj] = useState<{ skySession?: boolean; skySessioneventid?: string }>({})
  const apiCount = useRef(1)
  const selectMarketRef = useRef(selectMarket)
  selectMarketRef.current = selectMarket

  const filteredMatches = useMemo(
    () =>
      markets.map((m) => ({
        ...m,
        label: `${m.name}-${m.betfairId ?? ''}`,
      })),
    [markets],
  )

  const getAllSubMarket = useCallback(
    async (subMarketId?: string) => {
      if (!subMarketId) return
      const selectedMarket = selectMarketRef.current
      if (selectedMarket) {
        localStorage.storeSelectedMarket = JSON.stringify(selectedMarket)
      }
      try {
        const result = await sessionApi.getAllSubMarket(subMarketId)
        const data = result.data as {
          submarketList?: SubMarket[]
          runList?: RunItem[]
          rateList?: RateItem[]
        }
        if (data?.submarketList?.length) {
          const list = data.submarketList.map((m) => ({
            ...m,
            status: m.status === 'Disable' ? false : m.status === 'Enable' ? true : m.status,
          }))
          setSubMarkets(list)
          setRunList(data.runList ?? [])
          setRateList(data.rateList ?? [])
        } else {
          setSubMarkets('No SubMarket Found')
          setRunList(data?.runList ?? [])
          setRateList(data?.rateList ?? [])
        }
      } catch {
        toast.error('Failed to load submarkets')
      }
    },
    [toast],
  )

  const getOneMarket = useCallback(
    async (market: Market | null) => {
      if (!market?._id) return
      try {
        const result = await sessionApi.getOneMarket({ markets: market._id })
        if (result.status === 200) {
          const data = result.data as Market | null
          if (!data) {
            if (apiCount.current < 4) {
              const n = apiCount.current
              apiCount.current++
              window.setTimeout(() => void getOneMarket(selectMarketRef.current), n * 1000)
            }
            return
          }
          setMarketFlags((prev) => ({
            ...prev,
            ballChalu: !!data.ballChalu,
            isMarketSuspended: !!data.isSuspended,
            betDelay: data.betDelay,
            _id: data._id,
          }))
          setSkyObj({
            skySession: !!data.skySession,
            skySessioneventid: data.skySessioneventid as string | undefined,
          })
          apiCount.current = 1
        } else if (apiCount.current < 4) {
          const n = apiCount.current
          apiCount.current++
          window.setTimeout(() => void getOneMarket(selectMarketRef.current), n * 1000)
        }
      } catch {
        if (apiCount.current < 4) {
          const n = apiCount.current
          apiCount.current++
          window.setTimeout(() => void getOneMarket(selectMarketRef.current), n * 1000)
        }
      }
    },
    [],
  )

  useEffect(() => {
    void sessionApi.getAllMarkets().then((res) => {
      const data = res.data as Market[]
      if (Array.isArray(data)) setMarkets(data)
    })
    void getUserProfile().then((u) => setAccessLevel(u?.accessLevel ?? ''))

    try {
      if (localStorage.storeSelectedMarket) {
        const stored = JSON.parse(localStorage.storeSelectedMarket) as Market | string
        if (stored && typeof stored === 'object' && stored._id) {
          setSelectMarket(stored)
          setSelected(true)
          setForm((f) => ({ ...f, selectMarket: stored }))
          void getAllSubMarket(stored._id)
          void getOneMarket(stored)
        }
      }
    } catch {
      /* ignore */
    }
  }, [getAllSubMarket, getOneMarket])

  useEffect(() => {
    if (!reloadPage) return
    const id = window.setInterval(() => {
      setCounter((c) => {
        if (c <= 1) {
          const m = selectMarketRef.current
          if (m?._id) {
            void getAllSubMarket(m._id)
            void getOneMarket(m)
          }
          return 20
        }
        return c - 1
      })
    }, 1000)
    return () => window.clearInterval(id)
  }, [reloadPage, getAllSubMarket, getOneMarket])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (
        shouldIgnoreMarketSuspendHotkeys({
          accessLevel,
          selected,
          marketId: selectMarketRef.current?._id,
          createOpen,
          subMarketkey,
          targetTag: (e.target as HTMLElement)?.tagName,
        })
      ) {
        return
      }
      if (e.key === 'r' || e.key === 'R') {
        e.preventDefault()
        void toggleSuspend()
      }
      if (e.key === 'b' || e.key === 'B') {
        e.preventDefault()
        void toggleBallRunning()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessLevel, selected, marketFlags, createOpen, subMarketkey])

  useEffect(() => {
    if (!createOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (!shouldSubmitCreateMarketOnEnter(createOpen, e.key, createSavingRef.current)) return
      e.preventDefault()
      checkBet(form.minBet, form.maxBet)
      void saveSubMarket()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOpen, form, incorrectBet, createSaving])

  const onSelectMarket = (id: string) => {
    const m = markets.find((x) => x._id === id) ?? null
    setSelectMarket(m)
    setSelected(!!m)
    setForm((f) => ({ ...f, selectMarket: m }))
    if (m) {
      localStorage.storeSelectedMarket = JSON.stringify(m)
      void getAllSubMarket(m._id)
      void getOneMarket(m)
    }
  }

  const toggleSuspend = async () => {
    if (!selectMarket?._id) return
    const prev = marketFlags
    const next = { ...marketFlags, ...nextMarketFlags(marketFlags, 'suspend') }
    setMarketFlags(next)
    try {
      await sessionApi.marketSuspended(selectMarket._id, next)
    } catch {
      setMarketFlags(prev)
      toast.error('Failed to update Suspend')
    }
  }

  const toggleBallRunning = async () => {
    if (!selectMarket?._id) return
    const prev = marketFlags
    const next = { ...marketFlags, ...nextMarketFlags(marketFlags, 'ball') }
    setMarketFlags(next)
    try {
      await sessionApi.marketSuspended(selectMarket._id, next)
    } catch {
      setMarketFlags(prev)
      toast.error('Failed to update Ball running')
    }
  }

  const saveSubMarket = async () => {
    if (!canStartCreateMarketSave(createSavingRef.current)) return
    if (!form.subMarketName || !form.selectMarket) {
      toast.error('Name required')
      return
    }
    if (incorrectBet) return
    createSavingRef.current = true
    setCreateSaving(true)
    try {
      await sessionApi.saveSubMarket({ ...form, selectMarket: form.selectMarket })
      toast.success('SubMarket Saved Successfully')
      setCreateOpen(false)
      setForm((f) => ({
        ...f,
        subMarketName: '',
        maxBet: 0,
        minBet: 1,
        suspendMaxLimit: 0,
        notification: '',
      }))
      if (selectMarket?._id) void getAllSubMarket(selectMarket._id)
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } }; data?: { message?: string } })
          ?.response?.data?.message ??
        (err as { data?: { message?: string } })?.data?.message ??
        'Save failed'
      toast.error(msg)
    } finally {
      createSavingRef.current = false
      setCreateSaving(false)
    }
  }

  const checkBet = (minBet: number, maxBet: number) => {
    if (minBet > 0 && maxBet === 0) setIncorrectBet(false)
    else if (minBet <= 0) setIncorrectBet(true)
    else setIncorrectBet(false)
  }

  const hasSubMarkets = Array.isArray(subMarkets) && subMarkets.length > 0

  return (
    <div className="sm-dashboard">
      <PageHead title="Cricket" />
      <div className="sm-row sm-match-bar">
        <select
          className="w7-input sm-match-select"
          value={selectMarket?._id ?? ''}
          onChange={(e) => onSelectMarket(e.target.value)}
        >
          <option value="">Please select match</option>
          {filteredMatches.map((m) => (
            <option key={m._id} value={m._id}>
              {m.label}
            </option>
          ))}
        </select>
        <div className="sm-row sm-reload">
          {skyObj.skySession && accessLevel === 'admin' ? (
            <label className="sm-row">
              sky eventid:
              <Input
                value={skyObj.skySessioneventid ?? ''}
                onChange={(e) => setSkyObj((s) => ({ ...s, skySessioneventid: e.target.value }))}
                onBlur={() => {
                  if (!selectMarket?._id) return
                  void sessionApi
                    .skySessioneventid({
                      marketId: selectMarket._id,
                      skySessioneventid: skyObj.skySessioneventid,
                    })
                    .catch((err: { response?: { data?: { message?: string } } }) =>
                      toast.error(err?.response?.data?.message ?? 'Update failed'),
                    )
                }}
              />
            </label>
          ) : null}
          <span className="sm-reload-label">
            Reload {reloadPage ? `In: ${counter}` : ''}
          </span>
          <Toggle
            checked={!!reloadPage}
            onChange={(v) => {
              setReloadPage(v)
              localStorage.setItem('reloadStatus', String(v))
              setCounter(20)
            }}
            label="Auto reload"
          />
        </div>
      </div>

      <div className="sm-card">
        <div className="sm-row sm-market-row">
          <strong className="sm-market-title">Market Name</strong>
          <input
            className="w7-input sm-market-input"
            value={selectMarket?.name ?? ''}
            readOnly
          />
          <Button variant="primary" disabled={!selected} onClick={() => setCreateOpen(true)}>
            Create market
          </Button>
        </div>

        {selected && accessLevel === 'admin' ? (
          <div
            className="sm-row"
            style={{ borderBottom: '1px solid var(--w7-border-subtle)', paddingBottom: 12, marginBottom: 12 }}
          >
            <label className="sm-toggle" title="r">
              Suspend
              <Toggle
                checked={!!marketFlags.isMarketSuspended}
                onChange={() => void toggleSuspend()}
                label="Suspend"
              />
            </label>
            <label className="sm-toggle" title="b">
              Ball running
              <Toggle
                checked={!!marketFlags.ballChalu}
                onChange={() => void toggleBallRunning()}
                label="Ball running"
              />
            </label>
            <label className="sm-row">
              Bet Delay
              <Input
                value={String(marketFlags.betDelay ?? '')}
                onChange={(e) => setMarketFlags((m) => ({ ...m, betDelay: e.target.value }))}
                onBlur={() => {
                  void sessionApi.changeMarketBetDelay(marketFlags, marketFlags.betDelay).then(() => {
                    if (selectMarket?._id) void getAllSubMarket(selectMarket._id)
                  })
                }}
              />
            </label>
          </div>
        ) : null}

        {!selected ? (
          <p className="w7-dim" style={{ margin: 0 }}>
            Please choose a market to see submarkets.
          </p>
        ) : hasSubMarkets && selectMarket ? (
          subMarkets.map((sm, i) => (
            <ScoreDashboard
              key={sm._id || `${sm.marketId}-${i}`}
              subMarketData={{
                subMarketShow: selectMarket,
                subMarketDetails: sm,
                rateList,
                runList,
              }}
              onRefresh={() => {
                if (selectMarket._id) void getAllSubMarket(selectMarket._id)
              }}
            />
          ))
        ) : (
          <h3 className="sm-empty-submarket">NO SUB MARKET FOUND</h3>
        )}
      </div>

      <Modal
        open={createOpen}
        onClose={() => {
          if (createSavingRef.current) return
          setCreateOpen(false)
        }}
        title="Add New Market"
        footer={
          <div className="sm-row sm-row-end">
            <Button
              disabled={createSaving}
              onClick={() => {
                if (createSavingRef.current) return
                setCreateOpen(false)
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              busy={createSaving}
              onClick={() => {
                checkBet(form.minBet, form.maxBet)
                void saveSubMarket()
              }}
            >
              OK
            </Button>
          </div>
        }
      >
        <div className="w7-col" style={{ gap: 12 }}>
          <label>
            Session Name
            <Input
              value={form.subMarketName}
              onChange={(e) => setForm((f) => ({ ...f, subMarketName: e.target.value }))}
              maxLength={100}
              required
            />
          </label>
          <div className="sm-row">
            <label style={{ flex: 1 }}>
              Min-Bet
              <Input
                type="number"
                value={String(form.minBet)}
                onChange={(e) => {
                  const minBet = Number(e.target.value)
                  setForm((f) => ({ ...f, minBet }))
                  checkBet(minBet, form.maxBet)
                }}
              />
            </label>
            <label style={{ flex: 1 }}>
              Max-Bet
              <select
                className="w7-input"
                value={form.maxLimitsType}
                onChange={(e) => setForm((f) => ({ ...f, maxLimitsType: e.target.value }))}
              >
                {MAX_LIMIT_OPTIONS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {incorrectBet ? (
            <p style={{ color: 'var(--w7-loss)' }}>Min-Bet should be less then Max-bet</p>
          ) : null}
          <label>
            Max Limit To Suspend
            <Input
              type="number"
              value={String(form.suspendMaxLimit)}
              onChange={(e) => setForm((f) => ({ ...f, suspendMaxLimit: e.target.value }))}
            />
          </label>
          <label>
            Notification Text
            <Input
              value={form.notification}
              onChange={(e) => setForm((f) => ({ ...f, notification: e.target.value }))}
            />
          </label>
        </div>
      </Modal>
    </div>
  )
}
